import {
  Column,
  ColumnSizingState,
  ColumnVisibilityState,
  flexRender,
  Header,
  OnChangeFn,
  Row,
  RowData,
  SortDirection,
  SortingState,
  useTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import {
  DragEvent,
  KeyboardEvent,
  memo,
  MouseEvent,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ColumnFilterControl,
  type ColumnFilter,
  type FilterSpec,
  type FilterType,
} from "@tsmono/inspect-components/columnFilter";
import { isNewTabClick } from "@tsmono/react/components";
import { useMountEffect } from "@tsmono/react/hooks";
import { Icon } from "@tsmono/react/icons";
import { openHrefInNewTab } from "@tsmono/react/routing";
import { isRecord, isVscode } from "@tsmono/util";

import { useStableValue } from "../useStableValue";

import { computeAutoSizeWidth } from "./autoSize";
import { resolveColumnWidths } from "./columnFit";
import {
  dropIndicatorSide,
  moveColumn,
  resolveColumnOrder,
} from "./columnReorder";
import { ExtendedColumnDef } from "./columnTypes";
import styles from "./DataGrid.module.css";
import { resolveKeyboardNavTarget } from "./keyboardNav";
import { dataGridFeatures, DataGridFeatures } from "./tableFeatures";

const kRowHeight = 30;
const kPageJump = 10;
const kHeaderHeight = 25;
// Tall enough to host a rotated score-column label (≈92px of vertical
// extent for a 130px label at 45°) plus breathing room.
const kRotatedHeaderHeight = 115;

/** Full-text header tooltip (native `title`), shown on hover — useful when the
 *  header label is truncated (regular ellipsis or a narrow rotated label).
 *  Prefers an explicit `headerTitle`, else the string header text. */
function resolveHeaderTitle<TRow extends RowData>(
  columnDef: ExtendedColumnDef<TRow>
): string | undefined {
  if (columnDef.headerTitle) return columnDef.headerTitle;
  return typeof columnDef.header === "string" ? columnDef.header : undefined;
}

/** Keyboard activation for a sortable header: Enter or Space cycles the sort,
 *  matching the pointer `onClick`. Stops propagation so the grid container's
 *  key handler doesn't also treat Enter/Space as "activate the selected row".
 *  Shift/Cmd/Ctrl carry through to the toggle handler for multi-sort. */
function makeSortKeyDownHandler<TRow extends RowData>(
  header: Header<DataGridFeatures, TRow, unknown>
) {
  return (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    e.stopPropagation();
    header.column.getToggleSortingHandler()?.(e);
  };
}

/** Mouse-down handler for a column's resize divider. It cancels the press's
 *  default action: once the resize moves the divider off the press point,
 *  that point is over a draggable header label (the column's own when
 *  widening, its neighbour's when narrowing), and the browser would start a
 *  column drag from it — reordering columns and swallowing the mouseup that
 *  ends the resize, which then follows the pointer. Cancelling also skips
 *  the press's default focus, so focus the grid as the press used to (arrow
 *  keys keep working after a resize). */
function startColumnResize<TRow extends RowData>(
  header: Header<DataGridFeatures, TRow, unknown>
): (e: MouseEvent<HTMLElement>) => void {
  const resize = header.getResizeHandler();
  return (e) => {
    e.preventDefault();
    const grid = e.currentTarget.closest('[role="grid"]');
    if (grid instanceof HTMLElement) grid.focus({ preventScroll: true });
    resize(e);
  };
}

/** Rendered width of an element's contents, measured with a Range so bare
 *  text nodes count (a cell's clientWidth is the truncated box, not the
 *  content). Guarded: jsdom's Range has no layout — measure as 0 there. */
function measureContentWidth(el: Element): number {
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    return range.getBoundingClientRect().width;
  } catch {
    return 0;
  }
}

/** Header sort indicator: direction arrow plus, when set, this column's
 *  1-based position in a multi-column sort. Takes plain values rather than
 *  the header: `header` keeps its identity across sort changes, so the
 *  compiled component would reuse an indicator read off it. */
function SortIndicator({
  sorted,
  sortOrder,
}: {
  sorted: false | SortDirection;
  sortOrder: number | undefined;
}): ReactElement | null {
  if (!sorted) return null;
  return (
    <span className={styles.sortIndicator}>
      {sortOrder !== undefined && (
        <span className={styles.sortOrder}>{sortOrder}</span>
      )}
      <Icon
        name={sorted === "asc" ? "arrowUp" : "arrowDown"}
        className={styles.sortIcon}
      />
    </span>
  );
}
// Extra scroll width past the last column so its rotated label, which
// fans up-and-right beyond the column edge, isn't clipped at max scroll.
const kRotatedTrailingPad = 95;
// Extra render width for a normal column that directly follows a rotated
// block; paired with matching left padding (.afterRotatedGap) so the gap
// separates the columns without shrinking the content box.
const kAfterRotatedGap = 24;
// Width reserved from the fit-to-width target: the last column's resize
// handle overhangs its cell by 3px (.resizeHandle right: -3px), and columns
// fitted flush to the container would let it trip a permanent horizontal
// scrollbar. Rows stretch over the reserve (min-width: 100%), so it's
// invisible.
const kFitSlack = 4;

/** Per visible column, in display order: rendered width (including any
 *  after-rotated gap) and, when left-pinned, sticky offset. `byId` is a Map
 *  because column ids can come from log content (scorer names). */
interface ColumnLayout<TRow extends RowData> {
  /** The table's visible leaf columns. Compared by identity so the layout
   *  also changes when column defs do (new cell renderers or styles at the
   *  same widths), since rows read their cells through it. */
  columns: Column<DataGridFeatures, TRow, unknown>[];
  byId: ReadonlyMap<string, { width: number; pinnedLeft: number | undefined }>;
}

function buildColumnLayout<TRow extends RowData>(
  columns: Column<DataGridFeatures, TRow, unknown>[],
  afterRotatedIds: ReadonlySet<string>
): ColumnLayout<TRow> {
  return {
    columns,
    byId: new Map(
      columns.map((column) => [
        column.id,
        {
          width:
            column.getSize() +
            (afterRotatedIds.has(column.id) ? kAfterRotatedGap : 0),
          pinnedLeft:
            column.getIsPinned() === "start"
              ? column.getStart("start")
              : undefined,
        },
      ])
    ),
  };
}

function isSameColumnLayout<TRow extends RowData>(
  a: ColumnLayout<TRow>,
  b: ColumnLayout<TRow>
): boolean {
  if (a.columns !== b.columns) return false;
  for (const [id, layout] of a.byId) {
    const other = b.byId.get(id);
    if (
      other?.width !== layout.width ||
      other.pinnedLeft !== layout.pinnedLeft
    ) {
      return false;
    }
  }
  return true;
}

export interface DataGridProps<TRow extends RowData> {
  data: TRow[];
  columns: ExtendedColumnDef<TRow>[];
  getRowId: (row: TRow) => string;
  /** Controlled column visibility (keyed by column id). Owned by the caller. */
  columnVisibility?: ColumnVisibilityState;
  /** Controlled sort state. Rows arrive already sorted (manualSorting); this
   *  drives only the header indicators. */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  /** Controlled column widths (keyed by column id). */
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: (sizing: ColumnSizingState) => void;
  /** Controlled column order (leaf column ids). Ids missing from the list
   *  render after the listed ones in definition order; stale ids are
   *  ignored — so a persisted order survives columns appearing/disappearing
   *  (e.g. per-eval scorer columns). */
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  /** Controlled per-column filters (keyed by column id). */
  columnFilters?: Record<string, ColumnFilter>;
  onColumnFilterChange?: (
    columnId: string,
    filterType: FilterType,
    spec: FilterSpec | null
  ) => void;
  /** Hide all column filter funnels (e.g. while the active filter expression
   *  is not representable as per-column filters). The owner's `columnFilters`
   *  still apply to its rows; this only removes the header entry points. */
  hideColumnFilters?: boolean;
  /** Row id to render as selected and keep scrolled into view. */
  selectedRowId?: string;
  /** Report selection moves (arrow keys / click) instead of applying them:
   *  with this set the grid never mutates its own selection — the consumer
   *  owns it (e.g. zustand) and feeds it back through `selectedRowId`.
   *  Mirrors the `columnOrder`/`onColumnOrderChange` convention. */
  onSelectedRowChange?: (row: TRow) => void;
  /** External ref to the scroll container, attached alongside the internal
   *  one so a parent can observe scrolling (e.g. title-bar collapse-on-scroll
   *  via `useScrollDirection`). */
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** Plain left-click / Enter on a row. */
  onRowActivate: (row: TRow) => void;
  /** Link target for a row. When set, the row renders as an `<a>` so native
   *  link gestures (cmd/ctrl/shift/middle-click, context menu, hover
   *  preview) open it in a new tab; plain clicks still go to
   *  `onRowActivate`. Cmd/ctrl/shift+Enter opens it in a new tab too.
   *  Ignored inside the VS Code webview, where browser tabs don't exist. */
  getRowHref?: (row: TRow) => string | undefined;
  rowHeight?: number;
  headerHeight?: number;
  /** Tall, wrapping rows (list mode). Top-aligns cell content so a single-line
   *  cell sits at the first line of its multiline neighbours rather than
   *  floating in the vertical centre of the tall row. */
  multiline?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  /** Focus the grid container on mount so arrow-key navigation works
   *  immediately — e.g. the log list, where returning from a log should
   *  land you on the restored selection ready to arrow up/down. */
  autoFocus?: boolean;
  /** Accessible name for the grid as a whole (applied as `aria-label` on the
   *  `role="grid"` container). ag-grid left the grid unnamed; supplying one
   *  lets screen readers announce which grid has focus. */
  ariaLabel?: string;
}

/**
 * Inspect-local DataGrid: a minimal TanStack Table wrapper with row
 * virtualization, controlled column visibility, fit-to-width column sizing
 * (flex weights or proportional scaling — see columnFit.ts; horizontal
 * scroll when minimums overflow), and single-row selection +
 * click-to-activate.
 *
 * Sorting, filtering, keyboard navigation, column resizing (drag +
 * double-click auto-size), pinning, reordering, and find are wired up.
 */
export function DataGrid<TRow extends RowData>({
  data,
  columns,
  getRowId,
  columnVisibility,
  sorting,
  onSortingChange,
  columnSizing,
  onColumnSizingChange,
  columnOrder,
  onColumnOrderChange,
  columnFilters,
  onColumnFilterChange,
  hideColumnFilters,
  selectedRowId,
  onSelectedRowChange,
  scrollRef,
  onRowActivate,
  getRowHref: getRowHrefProp,
  rowHeight = kRowHeight,
  headerHeight = kHeaderHeight,
  multiline = false,
  loading = false,
  emptyMessage = "No matching items",
  className,
  autoFocus = false,
  ariaLabel,
}: DataGridProps<TRow>): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const getRowHref = isVscode() ? undefined : getRowHrefProp;

  // Attach both the internal scroll ref (virtualizer / focus / scroll-into-
  // view) and the optional external one to the container element.
  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      containerRef.current = el;
      if (scrollRef) scrollRef.current = el;
    },
    [scrollRef]
  );

  // Selection is controlled when `onSelectedRowChange` is set: moves are
  // reported to the consumer (the selection's owner, e.g. zustand) and only
  // `selectedRowId` renders — the grid never shadows external selection
  // state. Without it, selection is a grid-local cursor seeded/synced from
  // `selectedRowId` (e.g. the currently-open log) so the highlight survives
  // navigation back to the list.
  const [internalSelectedId, setInternalSelectedId] = useState<
    string | undefined
  >(selectedRowId);
  // Adjust-on-prop-change during render (react.dev pattern) rather than in an
  // effect, so a changed `selectedRowId` never paints a stale-selection frame.
  const [prevSelectedRowId, setPrevSelectedRowId] = useState(selectedRowId);
  if (selectedRowId !== prevSelectedRowId) {
    setPrevSelectedRowId(selectedRowId);
    if (onSelectedRowChange === undefined && selectedRowId !== undefined) {
      setInternalSelectedId(selectedRowId);
    }
  }
  const selectedId = onSelectedRowChange ? selectedRowId : internalSelectedId;

  // Focus the grid on mount when requested so arrow-key navigation is live
  // without a click first (e.g. returning from a log to the restored
  // selection). Runs once — the log list remounts on scope change via
  // `key`, so a new scope re-focuses too.
  useMountEffect(() => {
    if (autoFocus) containerRef.current?.focus();
  });

  const selectRow = useCallback(
    (rowId: string, row: TRow) => {
      if (onSelectedRowChange) {
        onSelectedRowChange(row);
      } else {
        setInternalSelectedId(rowId);
      }
    },
    [onSelectedRowChange]
  );

  // Sorting is done by the caller (rows arrive pre-sorted); the table only
  // tracks sort state to drive header indicators (manualSorting).
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      if (!onSortingChange) return;
      onSortingChange(
        typeof updater === "function" ? updater(sorting ?? []) : updater
      );
    },
    [onSortingChange, sorting]
  );

  // Column sizing is controlled by the caller when `onColumnSizingChange` is
  // provided (log list persists per scope; samples keeps it in local state);
  // otherwise the grid manages it internally so resizing still works.
  const [internalSizing, setInternalSizing] = useState<ColumnSizingState>({});
  const effectiveSizing = columnSizing ?? internalSizing;
  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback(
    (updater) => {
      const next =
        typeof updater === "function" ? updater(effectiveSizing) : updater;
      if (onColumnSizingChange) onColumnSizingChange(next);
      else setInternalSizing(next);
    },
    [effectiveSizing, onColumnSizingChange]
  );

  // Column order: controlled when `onColumnOrderChange` is provided (log
  // list persists per scope), else internal. The stored order is reconciled
  // against the current column defs on every render so it never goes stale.
  const [internalOrder, setInternalOrder] = useState<string[]>([]);
  const orderSource = columnOrder ?? internalOrder;
  const effectiveColumnOrder = useMemo(() => {
    const leafIds = columns
      .map((c) => c.id)
      .filter((id): id is string => id !== undefined);
    return resolveColumnOrder(orderSource, leafIds);
  }, [orderSource, columns]);
  const commitColumnOrder = useCallback(
    (next: string[]) => {
      if (onColumnOrderChange) onColumnOrderChange(next);
      else setInternalOrder(next);
    },
    [onColumnOrderChange]
  );

  // Left-pinned columns, declared on the defs. Feeding TanStack's
  // columnPinning state orders them before unpinned columns (overriding
  // columnOrder); rendering keeps them visible under horizontal scroll via
  // position: sticky. Pinned columns are immovable — excluded from
  // drag-reorder as both source and target (matching the AG grid).
  const columnPinning = useMemo(
    () => ({
      start: columns.flatMap((c) =>
        c.pinned === "start" && c.id !== undefined ? [c.id] : []
      ),
      end: [],
    }),
    [columns]
  );
  const pinnedStart = useMemo(
    () => new Set(columnPinning.start),
    [columnPinning]
  );

  // Drag-to-reorder. Native HTML5 drag on the header content (never the
  // resize handles, which are sibling elements): the browser's built-in
  // drag threshold keeps plain sort-clicks intact, and a real drag
  // suppresses the click. Drop targets are the header cells; the column
  // moves once, on drop, to the indicated edge.
  const [draggedColId, setDraggedColId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    colId: string;
    side: "left" | "right";
  } | null>(null);

  const dragGhostRef = useRef<HTMLElement | null>(null);
  const dragSessionRef = useRef<string | null>(null);
  const removeDragGhost = useCallback(() => {
    dragGhostRef.current?.remove();
    dragGhostRef.current = null;
  }, []);

  const handleHeaderDragOver = useCallback(
    (e: DragEvent<HTMLElement>, colId: string) => {
      // Pinned columns are not drop targets: skipping preventDefault leaves
      // the cell an invalid target (browser shows no-drop).
      if (!draggedColId || pinnedStart.has(colId)) return;
      // preventDefault marks the cell as a valid drop target.
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (draggedColId === colId) {
        setDropTarget(null);
        return;
      }
      const side = dropIndicatorSide(effectiveColumnOrder, draggedColId, colId);
      // dragover fires continuously — keep the previous state object when
      // nothing changed so we don't re-render the grid on every mousemove.
      setDropTarget((prev) =>
        prev && prev.colId === colId && prev.side === side
          ? prev
          : { colId, side }
      );
    },
    [draggedColId, effectiveColumnOrder, pinnedStart]
  );

  const handleHeaderDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    // dragleave also fires when moving onto the cell's own children.
    if (
      e.relatedTarget instanceof Node &&
      e.currentTarget.contains(e.relatedTarget)
    ) {
      return;
    }
    setDropTarget(null);
  }, []);

  const handleHeaderDrop = useCallback(
    (e: DragEvent<HTMLElement>, colId: string) => {
      e.preventDefault();
      if (draggedColId && !pinnedStart.has(colId)) {
        const next = moveColumn(effectiveColumnOrder, draggedColId, colId);
        if (next) commitColumnOrder(next);
      }
      setDraggedColId(null);
      setDropTarget(null);
    },
    [draggedColId, effectiveColumnOrder, commitColumnOrder, pinnedStart]
  );

  // Fires on the drag source after a drop OR a cancelled drag (Escape /
  // released outside a target) — the one reliable place to clear state.
  const handleHeaderDragEnd = useCallback(() => {
    dragSessionRef.current = null;
    removeDragGhost();
    setDraggedColId(null);
    setDropTarget(null);
  }, [removeDragGhost]);

  // Visible leaf defs in display order (pinned first, then columnOrder),
  // derived from props rather than the table because the fit-to-width pass
  // below feeds the table's own columnSizing state.
  const orderedVisibleDefs = useMemo(() => {
    const byId = new Map<string, ExtendedColumnDef<TRow>>();
    for (const c of columns) {
      if (c.id !== undefined) byId.set(c.id, c);
    }
    const rest = effectiveColumnOrder.filter((id) => !pinnedStart.has(id));
    return [...columnPinning.start, ...rest]
      .filter((id) => (columnVisibility?.[id] ?? true) && byId.has(id))
      .map((id) => byId.get(id)!);
  }, [
    columns,
    columnVisibility,
    effectiveColumnOrder,
    columnPinning,
    pinnedStart,
  ]);

  // Rotated headers (compact score columns) need a taller header row and
  // extra trailing scroll width so the last label isn't clipped. Normal
  // columns directly after a rotated block get indented (the previous
  // column's angled label anchors at the shared edge and fans over this
  // column's header, so its diagonal would slice through header text
  // sitting flush left). Only the header text is indented; body cells keep
  // the default inset but carry the same extra render width, so the column
  // edges stay aligned.
  const { anyRotated, afterRotatedIds } = useMemo(() => {
    const anyRotated = orderedVisibleDefs.some((c) => c.meta?.rotateHeader);
    const afterRotatedIds = new Set<string>();
    orderedVisibleDefs.forEach((c, i) => {
      const prev = orderedVisibleDefs[i - 1];
      if (!prev || c.id === undefined) return;
      if (prev.meta?.rotateHeader && !c.meta?.rotateHeader) {
        afterRotatedIds.add(c.id);
      }
    });
    return { anyRotated, afterRotatedIds };
  }, [orderedVisibleDefs]);
  const gapExtra = afterRotatedIds.size * kAfterRotatedGap;
  const effectiveHeaderHeight = anyRotated
    ? kRotatedHeaderHeight
    : headerHeight;
  const trailingPad = anyRotated ? kRotatedTrailingPad : 0;

  // Fit-to-width (see columnFit.ts): flex columns absorb leftover space;
  // with none visible, resizable columns scale proportionally. The container
  // width arrives via ResizeObserver (guarded: absent in jsdom), so the
  // first paint uses base sizes until the observer reports in — the AG grid
  // this replaced painted initial widths before fitting too.
  const [containerWidth, setContainerWidth] = useState(0);
  useMountEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      setContainerWidth(el.clientWidth);
    });
    observer.observe(el);
    setContainerWidth(el.clientWidth);
    return () => observer.disconnect();
  });
  const resolvedSizing = useMemo(() => {
    const fitColumns = orderedVisibleDefs.flatMap((c) =>
      c.id === undefined
        ? []
        : [
            {
              id: c.id,
              size: c.size,
              minSize: c.minSize,
              maxSize: c.maxSize,
              flex: c.flex,
            },
          ]
    );
    return resolveColumnWidths(
      fitColumns,
      containerWidth - gapExtra - trailingPad - kFitSlack,
      effectiveSizing
    );
  }, [
    orderedVisibleDefs,
    containerWidth,
    gapExtra,
    trailingPad,
    effectiveSizing,
  ]);

  // Double-click on a resize handle auto-sizes the column to its content
  // (the AG grid's built-in divider double-click). Only rendered cells are
  // measured — AG does the same — via a Range so plain text nodes measure
  // correctly. The result commits through the normal sizing path, so it
  // persists exactly like a drag-resize.
  const autoSizeColumn = useCallback(
    (column: Column<DataGridFeatures, TRow, unknown>) => {
      const container = containerRef.current;
      if (!container) return;
      const selector = `[role="gridcell"][data-col-id="${CSS.escape(column.id)}"]`;
      const cellWidths = Array.from(container.querySelectorAll(selector)).map(
        measureContentWidth
      );
      const headerEl = container.querySelector(
        `[data-header-col-id="${CSS.escape(column.id)}"]`
      );
      const def = column.columnDef as ExtendedColumnDef<TRow>;
      const width = computeAutoSizeWidth({
        cellWidths,
        headerWidth: headerEl ? measureContentWidth(headerEl) : 0,
        minSize: def.minSize,
        maxSize: def.maxSize,
      });
      handleColumnSizingChange((old) => ({ ...old, [column.id]: width }));
    },
    [handleColumnSizingChange]
  );

  const table = useTable({
    features: dataGridFeatures,
    data,
    columns,
    getRowId,
    manualSorting: true,
    // TanStack defaults non-string columns to descending-first; the AG grid
    // this replaced sorted every column ascending-first.
    sortDescFirst: false,
    enableMultiSort: true,
    // TanStack's default multi-sort trigger is shift-only; also accept
    // cmd/ctrl to match the AG grid this replaced. Read the modifier keys
    // structurally: JSX handlers deliver React SyntheticEvents here, which
    // carry the flags but are not instances of the native event classes.
    isMultiSortEvent: (e) => {
      if (!isRecord(e)) return false;
      return (
        e["shiftKey"] === true || e["metaKey"] === true || e["ctrlKey"] === true
      );
    },
    enableSortingRemoval: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      columnVisibility: columnVisibility ?? {},
      sorting: sorting ?? [],
      // The fit-resolved widths, not the sparse override map — the table
      // is the single source the render reads sizes from (getSize/getStart).
      columnSizing: resolvedSizing,
      columnOrder: effectiveColumnOrder,
      columnPinning,
    },
    onSortingChange: handleSortingChange,
    onColumnSizingChange: handleColumnSizingChange,
  });

  // `table` is a fresh object every render; its store is the stable handle.
  const tableStore = table.store;
  const handleHeaderDragStart = useCallback(
    (e: DragEvent<HTMLElement>, colId: string, label: string) => {
      // Backstop for `startColumnResize`: a drag that starts mid-resize would
      // take the mouseup that ends it. Cancelling dragstart is the one
      // drag-abort every browser honors.
      if (tableStore.state.columnResizing.isResizingColumn !== false) {
        e.preventDefault();
        return;
      }
      e.dataTransfer.effectAllowed = "move";
      // Firefox won't start a drag with an empty data store.
      e.dataTransfer.setData("text/plain", colId);
      // Chromium snapshots the drag image from the source element, which for
      // the 45°-rotated labels is unusable (transformed paint, huge bounds).
      // Hand it an unrotated chip with the column name instead — also what
      // the AG grid's ghost looked like. Guarded: jsdom has no setDragImage.
      if (typeof e.dataTransfer.setDragImage === "function") {
        const chip = document.createElement("div");
        chip.className = styles.dragGhost;
        chip.textContent = label;
        document.body.appendChild(chip);
        e.dataTransfer.setDragImage(chip, 12, 14);
        dragGhostRef.current = chip;
      }
      // Defer the state flip: it re-renders the header (dim the source, flip
      // rotated-label pointer-events) and mutating the DOM while Chromium is
      // still establishing the drag session aborts the drag outright. The
      // session ref voids the deferred set if the drag ends first.
      dragSessionRef.current = colId;
      setTimeout(() => {
        if (dragSessionRef.current === colId) setDraggedColId(colId);
      }, 0);
    },
    [tableStore]
  );

  const { rows } = table.getRowModel();
  const totalWidth = table.getTotalSize();

  const visibleColumns = table.getVisibleLeafColumns();
  // The sort-order number is noise for a single sort, so it only appears for
  // multi-sorts — matching the previous AG grid.
  const multiSorted = table.store.state.sorting.length > 1;

  // Header and body cells both place themselves from this, and rows must
  // read it rather than `getSize()`: widths can shift between columns at a
  // constant total (a resize absorbed by flex columns), which changes no
  // other GridRow prop, so a row reading the table would stay stale under
  // memo and the React Compiler. Stabilized so rows skip re-rendering on
  // unrelated grid-state changes.
  const columnLayout = useStableValue(
    buildColumnLayout(visibleColumns, afterRotatedIds),
    isSameColumnLayout
  );

  // The sticky header occupies layout space at the top of the scroll
  // container, so the virtualized rows start `headerHeight` px down. Two knobs
  // keep scrollToIndex in the same coordinate space as the DOM:
  //  - scrollMargin shifts the virtual offsets down by the header, so a row
  //    aligned to the bottom ("end"/"auto") isn't a header's-height too low
  //    (which clipped it at the bottom edge);
  //  - scrollPaddingStart reserves the header's height when aligning to the
  //    top ("start"/"auto"), so a row scrolled in from above sits below the
  //    sticky header instead of behind it.
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
    scrollMargin: effectiveHeaderHeight,
    scrollPaddingStart: effectiveHeaderHeight,
    getItemKey: (index) => rows[index]?.id ?? String(index),
  });

  // Keep the selected row visible when it changes from the outside. `rows`
  // is a read, not a trigger: during a live eval every poll tick lands a new
  // rows identity, and re-scrolling then would yank the viewport back to the
  // selection whenever the user has scrolled it off-screen. The ref marks a
  // selection as scrolled-to only once the row is actually found, so a
  // selection that points at a not-yet-loaded row still scrolls on the tick
  // where the row arrives.
  const scrolledToSelectedRef = useRef<string | undefined>(undefined);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (!selectedId) {
      scrolledToSelectedRef.current = undefined;
      return;
    }
    if (scrolledToSelectedRef.current === selectedId) return;
    const index = rows.findIndex((r) => r.id === selectedId);
    if (index !== -1) {
      rowVirtualizer.scrollToIndex(index, { align: "auto" });
      scrolledToSelectedRef.current = selectedId;
    }
  }, [selectedId, rows, rowVirtualizer]);

  const handleRowClick = useCallback(
    (e: MouseEvent<HTMLElement>, rowId: string, row: TRow) => {
      // A link rendered inside a cell (e.g. markdown in list mode) keeps its
      // own click rather than opening the row.
      const link =
        e.target instanceof Element ? e.target.closest("a[href]") : null;
      if (link && link !== e.currentTarget) return;
      // Pull focus to the grid so arrow-key navigation works after a click.
      // Clicking a row link focuses it, and focus would drop to <body> once
      // the virtualizer unmounts that row.
      containerRef.current?.focus();
      // A new-tab gesture is left to the row's native <a> (which opens a
      // background tab, unlike window.open); just move the selection onto
      // the row so it's clear which one was opened.
      if (isNewTabClick(e)) {
        selectRow(rowId, row);
        return;
      }
      e.preventDefault();
      selectRow(rowId, row);
      onRowActivate(row);
    },
    [onRowActivate, selectRow]
  );

  // Keyboard navigation (arrows / Home / End / PgUp-Dn move the selection;
  // Enter/Space activates), matching the prior AG-grid behavior. Bound to the
  // grid container (tabIndex=0); ignored while focus is in a form control so
  // typing in a filter popover doesn't move the selection.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const active = document.activeElement;
      if (
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.tagName === "SELECT")
      ) {
        return;
      }

      const rowCount = rows.length;
      if (rowCount === 0) return;

      const currentIndex = selectedId
        ? rows.findIndex((r) => r.id === selectedId)
        : -1;

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const row = currentIndex === -1 ? undefined : rows[currentIndex];
        if (!row) return;
        const href =
          e.key === "Enter" && (e.metaKey || e.ctrlKey || e.shiftKey)
            ? getRowHref?.(row.original)
            : undefined;
        if (href) openHrefInNewTab(href);
        else onRowActivate(row.original);
        return;
      }

      const target = resolveKeyboardNavTarget({
        key: e.key,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        currentIndex,
        rowCount,
        pageJump: kPageJump,
      });
      if (target === null) return;
      e.preventDefault();
      if (target === currentIndex) return;

      const targetRow = rows[target];
      if (!targetRow) return;
      // The selection change drives the scroll-into-view effect below; doing
      // the scroll here too is redundant (react-virtual keeps only the last
      // pending scrollToIndex, so the effect's call wins anyway).
      selectRow(targetRow.id, targetRow.original);
    },
    [rows, selectedId, onRowActivate, getRowHref, selectRow]
  );

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Polite live-region text. ag-grid maintained its own off-screen live region
  // that spoke row-count/sort changes; reproduce a concise equivalent so a
  // screen-reader user hears the result of filtering, sorting, and loading
  // (React only rewrites the text node — and thus only announces — on change).
  const statusMessage = useMemo(() => {
    if (loading) return "Loading…";
    if (rows.length === 0) return emptyMessage;
    const active = sorting ?? [];
    const sortDesc =
      active.length > 0
        ? `, sorted by ${active
            .map((s) => `${s.id} ${s.desc ? "descending" : "ascending"}`)
            .join(", ")}`
        : "";
    return `${rows.length} ${rows.length === 1 ? "row" : "rows"}${sortDesc}`;
  }, [loading, rows.length, emptyMessage, sorting]);

  // aria-rowcount includes the header row; data rows are indexed from 2.
  const ariaRowCount = rows.length + 1;
  const ariaColCount = visibleColumns.length;

  return (
    <div
      ref={setContainerRef}
      className={clsx(styles.container, className)}
      role="grid"
      aria-label={ariaLabel}
      aria-rowcount={ariaRowCount}
      aria-colcount={ariaColCount}
      aria-busy={loading || undefined}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.srStatus} role="status" aria-live="polite">
        {statusMessage}
      </div>
      <div
        className={clsx(styles.table, multiline && styles.multiline)}
        // Add the rotated-label trailing room to the width, not as padding:
        // `.table` is `box-sizing: border-box`, so `paddingRight` would shrink
        // the content box and make the full-width rows overflow it — a second,
        // nested horizontal scrollbar. Widening keeps a single scroll extent.
        style={{ width: totalWidth + gapExtra + trailingPad }}
      >
        <div
          className={clsx(
            styles.thead,
            draggedColId !== null && styles.theadDragging
          )}
          style={{ height: effectiveHeaderHeight }}
          role="rowgroup"
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <div
              key={headerGroup.id}
              className={styles.headerRow}
              role="row"
              aria-rowindex={1}
            >
              {headerGroup.headers.map((header, colIndex) => {
                const columnDef = header.column
                  .columnDef as ExtendedColumnDef<TRow>;
                const filterSpec =
                  columnFilters?.[header.column.id]?.spec ?? null;
                const isDragSource = draggedColId === header.column.id;
                const dropSide =
                  dropTarget?.colId === header.column.id
                    ? dropTarget.side
                    : null;
                const sorted = header.column.getIsSorted();
                const sortIndex = header.column.getSortIndex();
                const sortOrder =
                  multiSorted && sortIndex >= 0 ? sortIndex + 1 : undefined;

                // Rotated (compact score) header: a 45° label hosting text +
                // sort caret + filter funnel. Rendered by a subcomponent so
                // the filter popover can anchor to a non-rotated element at
                // the cell's bottom — placing it below the header (like the
                // AG grid) rather than over the headers.
                if (columnDef.meta?.rotateHeader) {
                  return (
                    <RotatedHeaderCell
                      key={header.id}
                      header={header}
                      width={columnLayout.byId.get(header.column.id)?.width}
                      sorted={sorted}
                      sortOrder={sortOrder}
                      ariaColIndex={colIndex + 1}
                      filterSpec={filterSpec}
                      onColumnFilterChange={onColumnFilterChange}
                      hideColumnFilters={hideColumnFilters}
                      isDragSource={isDragSource}
                      dropSide={dropSide}
                      onHeaderDragStart={handleHeaderDragStart}
                      onHeaderDragEnd={handleHeaderDragEnd}
                      onHeaderDragOver={handleHeaderDragOver}
                      onHeaderDragLeave={handleHeaderDragLeave}
                      onHeaderDrop={handleHeaderDrop}
                      onAutoSize={() => autoSizeColumn(header.column)}
                    />
                  );
                }

                const align = columnDef.meta?.align;
                const filterType = columnDef.meta?.filterType;
                const pinned = header.column.getIsPinned() === "start";
                const sortCaret = (
                  <SortIndicator sorted={sorted} sortOrder={sortOrder} />
                );
                const headerLabel = header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    );
                const filterControl =
                  columnDef.meta?.filterable &&
                  filterType &&
                  !hideColumnFilters ? (
                    <ColumnFilterControl
                      columnId={header.column.id}
                      filterType={filterType}
                      operators={columnDef.meta.operators}
                      spec={filterSpec}
                      placement="bottom-start"
                      onChange={(spec) =>
                        onColumnFilterChange?.(
                          header.column.id,
                          filterType,
                          spec
                        )
                      }
                    />
                  ) : null;

                return (
                  // The cell widens the hit area for the mouse; the sortable
                  // headerContent button inside is the keyboard control, and
                  // the grid container owns focus for the whole widget.
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
                  <div
                    key={header.id}
                    className={clsx(
                      styles.headerCell,
                      header.column.getCanSort() && styles.headerCellSortable,
                      anyRotated && styles.headerCellTall,
                      afterRotatedIds.has(header.column.id) &&
                        styles.afterRotatedGap,
                      pinned && styles.headerCellPinned,
                      isDragSource && styles.headerCellDragSource,
                      dropSide === "left" && styles.headerCellDropLeft,
                      dropSide === "right" && styles.headerCellDropRight
                    )}
                    style={{
                      width: columnLayout.byId.get(header.column.id)?.width,
                      ...(pinned && {
                        position: "sticky" as const,
                        left: columnLayout.byId.get(header.column.id)
                          ?.pinnedLeft,
                        zIndex: 3,
                      }),
                    }}
                    title={resolveHeaderTitle(columnDef)}
                    role="columnheader"
                    aria-colindex={colIndex + 1}
                    aria-sort={
                      sorted === "asc"
                        ? "ascending"
                        : sorted === "desc"
                          ? "descending"
                          : "none"
                    }
                    onDragOver={(e) =>
                      handleHeaderDragOver(e, header.column.id)
                    }
                    onDragLeave={handleHeaderDragLeave}
                    onDrop={(e) => handleHeaderDrop(e, header.column.id)}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div
                      className={clsx(
                        styles.headerContent,
                        align === "center" && styles.headerCellCenter
                      )}
                      // A sortable header is a keyboard-operable control: Tab to
                      // it, Enter/Space to cycle sort (restoring the ag-grid
                      // behavior the click-only handler dropped). Non-sortable
                      // headers stay out of the tab order.
                      {...(header.column.getCanSort() && {
                        role: "button",
                        tabIndex: 0,
                        onKeyDown: makeSortKeyDownHandler(header),
                      })}
                      draggable={!pinned}
                      onDragStart={
                        pinned
                          ? undefined
                          : (e) =>
                              handleHeaderDragStart(
                                e,
                                header.column.id,
                                resolveHeaderTitle(columnDef) ??
                                  header.column.id
                              )
                      }
                      onDragEnd={handleHeaderDragEnd}
                    >
                      <span
                        className={styles.headerText}
                        data-header-col-id={header.column.id}
                      >
                        {headerLabel}
                      </span>
                      {sortCaret}
                    </div>
                    {filterControl && (
                      // The cell owns the sort click; the filter control isn't
                      // a sort target, so stop its clicks from bubbling up.
                      <div
                        className={clsx(
                          styles.headerFilter,
                          filterSpec && styles.headerFilterActive
                        )}
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {filterControl}
                      </div>
                    )}
                    {header.column.getCanResize() && (
                      // Pointer-only drag handle; column widths also reset
                      // from the column menu, so nothing here is unreachable.
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                      <div
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${header.column.id}`}
                        className={clsx(
                          styles.resizeHandle,
                          anyRotated && styles.resizeHandleTall,
                          header.column.getIsResizing() &&
                            styles.resizeHandleActive
                        )}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={startColumnResize(header)}
                        onTouchStart={header.getResizeHandler()}
                        onDoubleClick={() => autoSizeColumn(header.column)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div
          className={styles.tbody}
          style={{
            height: Math.max(0, totalSize - effectiveHeaderHeight),
            width: totalWidth + gapExtra,
          }}
          role="rowgroup"
        >
          {virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index];
            if (!row) return null;
            return (
              <GridRow
                key={row.id}
                row={row}
                // aria-rowindex is 1-based over all rows incl. the header row
                // (index 1), so the first data row is 2.
                ariaRowIndex={virtualRow.index + 2}
                isSelected={row.id === selectedId}
                rowHeight={rowHeight}
                width={totalWidth + gapExtra}
                // scrollMargin shifts virtual offsets by the header height;
                // the tbody already sits below the in-flow header, so
                // subtract it.
                top={virtualRow.start - effectiveHeaderHeight}
                columnLayout={columnLayout}
                href={getRowHref?.(row.original)}
                onRowClick={handleRowClick}
              />
            );
          })}
        </div>
      </div>
      {rows.length === 0 && (
        <div className={styles.empty}>
          {loading ? "Loading…" : emptyMessage}
        </div>
      )}
    </div>
  );
}

interface GridRowProps<TRow extends RowData> {
  row: Row<DataGridFeatures, TRow>;
  ariaRowIndex: number;
  isSelected: boolean;
  rowHeight: number;
  width: number;
  top: number;
  /** Read for every cell, so a change to it re-renders the row's cells
   *  under memo and the React Compiler alike — including visibility, order,
   *  and column-def changes that don't move the row's total `width`. */
  columnLayout: ColumnLayout<TRow>;
  href?: string;
  onRowClick: (e: MouseEvent<HTMLElement>, rowId: string, row: TRow) => void;
}

function GridRowInner<TRow extends RowData>({
  row,
  ariaRowIndex,
  isSelected,
  rowHeight,
  width,
  top,
  columnLayout,
  href,
  onRowClick,
}: GridRowProps<TRow>): ReactElement {
  const className = clsx(styles.row, isSelected && styles.rowSelected);
  const style = {
    height: rowHeight,
    width,
    transform: `translateY(${top}px)`,
  };
  const handleClick = (e: MouseEvent<HTMLElement>) =>
    onRowClick(e, row.id, row.original);
  const cells = row.getVisibleCells().map((cell, colIndex) => {
    const cellDef = cell.column.columnDef as ExtendedColumnDef<TRow>;
    const align = cellDef.meta?.align;
    const cellStyle = cellDef.meta?.cellStyle?.(row.original);
    const layout = columnLayout.byId.get(cell.column.id);
    const pinned = layout?.pinnedLeft !== undefined;
    return (
      <div
        key={cell.id}
        className={clsx(
          styles.cell,
          align === "center" && styles.cellCenter,
          pinned && styles.cellPinned
        )}
        style={{
          width: layout?.width,
          ...(pinned && {
            position: "sticky" as const,
            left: layout.pinnedLeft,
            zIndex: 1,
          }),
          ...cellStyle,
        }}
        title={cellDef.titleValue?.(row.original)}
        role="gridcell"
        aria-colindex={colIndex + 1}
        data-col-id={cell.column.id}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    );
  });

  if (href) {
    return (
      // A real link so the browser owns new-tab gestures. Keyboard focus and
      // activation stay with the grid container (arrows / Enter), so the row
      // is kept out of the tab order; draggable={false} stops a mouse drag
      // from dragging the URL. List-mode markdown cells can nest their own
      // <a> in here (invalid HTML, but the innermost link owns activation and
      // handleRowClick defers to it).
      <a
        className={className}
        style={style}
        href={href}
        tabIndex={-1}
        draggable={false}
        onClick={handleClick}
        onAuxClick={(e) => {
          if (e.button === 1) handleClick(e);
        }}
        role="row"
        aria-rowindex={ariaRowIndex}
        aria-selected={isSelected}
      >
        {cells}
      </a>
    );
  }
  return (
    // Row selection from the keyboard is the grid container's arrow-key
    // handler; the row click is the mouse path onto the same action.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
    <div
      className={className}
      style={style}
      onClick={handleClick}
      role="row"
      aria-rowindex={ariaRowIndex}
      aria-selected={isSelected}
    >
      {cells}
    </div>
  );
}

/**
 * Memoized row: skips re-rendering when a grid-level state change (selection
 * move, header drag-over, filter popover) doesn't touch this row's props.
 * Cell contents live in `row`, whose identity TanStack preserves while `data`
 * is unchanged. React.memo erases generics, so restore the signature.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- React.memo erases the generic parameter; there is no way to say "the same generic signature, memoized"
const GridRow = memo(GridRowInner) as typeof GridRowInner;

/**
 * Rotated (compact score) header cell. The 45° label hosts the text, sort
 * caret, and filter funnel. The filter popover anchors to a hidden,
 * non-rotated element at the cell's bottom so it opens below the header
 * (under the column) instead of over the headers next to the funnel.
 */
function RotatedHeaderCell<TRow extends RowData>({
  header,
  width,
  sorted,
  sortOrder,
  ariaColIndex,
  filterSpec,
  onColumnFilterChange,
  hideColumnFilters,
  isDragSource,
  dropSide,
  onHeaderDragStart,
  onHeaderDragEnd,
  onHeaderDragOver,
  onHeaderDragLeave,
  onHeaderDrop,
  onAutoSize,
}: {
  header: Header<DataGridFeatures, TRow, unknown>;
  /** From the grid's column layout, not `header.getSize()`: `header` keeps
   *  its identity across resizes, so the compiled component would reuse a
   *  width read off it. */
  width: number | undefined;
  /** From the grid, like `width`: `header` keeps its identity across sort
   *  changes too. */
  sorted: false | SortDirection;
  sortOrder: number | undefined;
  ariaColIndex: number;
  filterSpec: FilterSpec | null;
  onColumnFilterChange?: (
    columnId: string,
    filterType: FilterType,
    spec: FilterSpec | null
  ) => void;
  hideColumnFilters?: boolean;
  isDragSource: boolean;
  dropSide: "left" | "right" | null;
  onHeaderDragStart: (
    e: DragEvent<HTMLElement>,
    colId: string,
    label: string
  ) => void;
  onHeaderDragEnd: () => void;
  onHeaderDragOver: (e: DragEvent<HTMLElement>, colId: string) => void;
  onHeaderDragLeave: (e: DragEvent<HTMLElement>) => void;
  onHeaderDrop: (e: DragEvent<HTMLElement>, colId: string) => void;
  onAutoSize: () => void;
}): ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const columnDef = header.column.columnDef as ExtendedColumnDef<TRow>;
  const filterType = columnDef.meta?.filterType;
  const headerLabel = header.isPlaceholder
    ? null
    : flexRender(header.column.columnDef.header, header.getContext());

  return (
    // Drag/drop target only; the rotated label inside is the sortable
    // control and the grid container owns focus.
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      className={clsx(
        styles.headerCell,
        styles.headerCellRotated,
        isDragSource && styles.headerCellDragSource,
        dropSide === "left" && styles.headerCellDropLeft,
        dropSide === "right" && styles.headerCellDropRight
      )}
      style={{ width }}
      role="columnheader"
      aria-colindex={ariaColIndex}
      aria-sort={
        sorted === "asc"
          ? "ascending"
          : sorted === "desc"
            ? "descending"
            : "none"
      }
      // Drop targeting works during a drag because .theadDragging flips this
      // cell's pointer-events back on (see DataGrid.module.css).
      onDragOver={(e) => onHeaderDragOver(e, header.column.id)}
      onDragLeave={onHeaderDragLeave}
      onDrop={(e) => onHeaderDrop(e, header.column.id)}
    >
      {/* Sortable columns get role/tabIndex/onKeyDown from the spread below;
          for the rest this is a drag handle, not a control. The conditional
          spread is invisible to the a11y rules. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={clsx(
          styles.rotatedLabel,
          filterSpec && styles.rotatedLabelFiltered
        )}
        // Native tooltip lives on the label (the outer cell is
        // pointer-events: none, so a title there would never fire) — shows
        // the full name when the narrow rotated label truncates it.
        title={resolveHeaderTitle(columnDef)}
        {...(header.column.getCanSort() && {
          role: "button",
          tabIndex: 0,
          onKeyDown: makeSortKeyDownHandler(header),
        })}
        draggable
        onDragStart={(e) =>
          onHeaderDragStart(
            e,
            header.column.id,
            resolveHeaderTitle(columnDef) ?? header.column.id
          )
        }
        onDragEnd={onHeaderDragEnd}
        onClick={header.column.getToggleSortingHandler()}
      >
        <span className={styles.rotatedText}>{headerLabel}</span>
        <SortIndicator sorted={sorted} sortOrder={sortOrder} />
        {columnDef.meta?.filterable && filterType && !hideColumnFilters && (
          // The popover is portaled, but React events bubble through the
          // component tree — so clicks inside the filter would reach the
          // label's sort handler. Stop them here.
          <span
            className={styles.rotatedFilter}
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <ColumnFilterControl
              columnId={header.column.id}
              filterType={filterType}
              operators={columnDef.meta.operators}
              spec={filterSpec}
              anchorEl={anchorEl}
              placement="bottom-start"
              onChange={(spec) =>
                onColumnFilterChange?.(header.column.id, filterType, spec)
              }
            />
          </span>
        )}
      </div>
      <span
        ref={setAnchorEl}
        className={styles.rotatedFilterAnchor}
        aria-hidden="true"
      />
      {header.column.getCanResize() && (
        // Pointer-only drag handle — see the upright header above.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${header.column.id}`}
          className={clsx(
            styles.resizeHandle,
            styles.resizeHandleRotated,
            header.column.getIsResizing() && styles.resizeHandleActive
          )}
          onMouseDown={startColumnResize(header)}
          onTouchStart={header.getResizeHandler()}
          onDoubleClick={onAutoSize}
        />
      )}
    </div>
  );
}
